import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormFeedback } from '@/components/ui/form-feedback';
import { apiClient } from '@/lib/api-client';
import type { paths } from '@/lib/api-types-clinical-study';
import { formatDate } from '@/lib/intl';
import { usePendingWrite } from '@/lib/use-pending-write';
import { SubjectWorkspacePanel } from './SubjectWorkspacePanel';
import type { StudySubjectListItem, StudySubjectListSource } from './useStudySubjectList';

type CreatedSubject =
  paths['/v1/clinical-study/studies/{studyId}/subjects']['post']['responses'][201]['content']['application/json'];

// Resolution R11: a literal `as const` map, never a template-string `t()` key.
const LIFECYCLE_LABEL_KEY = {
  PROVISIONAL: 'subjects.lifecyclePROVISIONAL',
  ENROLLED: 'subjects.lifecycleENROLLED',
  CANCELLED: 'subjects.lifecycleCANCELLED',
} as const;
const LIFECYCLE_BADGE_TONE = {
  PROVISIONAL: 'warning',
  ENROLLED: 'success',
  CANCELLED: '',
} as const;

/**
 * `/clinical-study/studies/:studyId` composed section (FR-9.9, Resolution R2): a create action with
 * no input fields (no PII, mirrors the recon's own FR-9.9 client obligation) plus THE SUBJECT LIST
 * (SL-0045 Activity 7), which replaced the hand-typed Study Subject UUID resume input the section
 * carried while no list read existed. Per `docs/standards/screen-archetypes-and-interaction-
 * standard.md` § List, the row IS the affordance — clicking it opens the subject workspace, and
 * there is no separate "Apri" control. Rows are rendered as full-width buttons rather than through
 * the shared `DataTable`, which exposes no row-click prop and whose `<tr>` rows are not keyboard-
 * focusable; a button row is operable by keyboard by construction.
 *
 * All three read states render (§ Verification): `'loading'`, `'error'` with a retry control, and
 * `'ready'` — the last splitting into the no-subjects-exist empty state (whose copy points at the
 * creation CTA, per § List's empty-state rule) and the populated list. The list is newest-first as
 * the server orders it, and is reloaded after a successful create so the new subject appears at the
 * top without a page refresh.
 *
 * `openSubjectId` (renamed from `prefillResumeSubjectId` when the resume input was retired) opens
 * that subject directly — it is fed by `AtRiskVisitsSection`'s drill-down. The list itself arrives
 * as a prop: the page owns the single fetch, so a create here also refreshes the correction form's
 * picker instead of leaving it looking at a stale copy.
 *
 * Post-execution hardening (2026-07-27, WCAG 2.2 SC 3.3.1/4.1.3, Finding 4): the create-error and
 * created-success banners render through `FormFeedback` (`role="alert"`/`role="status"`).
 */
export function StudySubjectsSection({
  studyId,
  subjectList,
  openSubjectId = null,
  openRequest = 0,
  canReadSchedules,
  canReadPool,
}: {
  studyId: string;
  /** Owned and fetched ONCE by `ClinicalStudyDetailPage`, shared with `IdentifierCorrectionSection`
   * — see `useStudySubjectList`'s own note on why this is a prop and not a local hook call. */
  subjectList: StudySubjectListSource;
  openSubjectId?: string | null;
  /** Bumped by the page on every open request, so asking again for the subject already named in
   * `openSubjectId` (after opening another from the list) still opens it. */
  openRequest?: number;
  /** The page's permission flags for the subject panel's agenda (#893). */
  canReadSchedules: boolean;
  canReadPool: boolean;
}) {
  const { t } = useTranslation('clinical-study');
  const [activeSubjectId, setActiveSubjectId] = useState<string | null>(null);
  const [createdSubject, setCreatedSubject] = useState<CreatedSubject | null>(null);
  const [createApiError, setCreateApiError] = useState<string | null>(null);
  const [scrollTargetSubjectId, setScrollTargetSubjectId] = useState<string | null>(null);
  const listState = subjectList;
  const { pending, run } = usePendingWrite();

  useEffect(() => {
    if (openSubjectId !== null) {
      setCreatedSubject(null);
      setActiveSubjectId(openSubjectId);
      setScrollTargetSubjectId(openSubjectId);
    }
  }, [openSubjectId, openRequest]);

  // The workspace container does not exist in the DOM until the effect above has set
  // `activeSubjectId` and React has re-rendered, so the scroll waits for that render rather than
  // querying an element that is still absent.
  useEffect(() => {
    if (scrollTargetSubjectId !== null && activeSubjectId === scrollTargetSubjectId) {
      setScrollTargetSubjectId(null);
      document.getElementById('study-subject-workspace')?.scrollIntoView({ block: 'center' });
    }
  }, [scrollTargetSubjectId, activeSubjectId]);

  const onCreate = async () => {
    setCreateApiError(null);
    const { data, error, response } = await apiClient.POST(
      '/v1/clinical-study/studies/{studyId}/subjects',
      { params: { path: { studyId } } },
    );
    if (error !== undefined || !response.ok || data === undefined) {
      setCreateApiError(error?.detail ?? t('subjects.genericError'));
      return;
    }
    setCreatedSubject(data);
    setActiveSubjectId(data.id);
    listState.reload();
    toast.success(t('subjects.createdToast'));
  };

  const onOpen = (subject: StudySubjectListItem) => {
    setCreatedSubject(null);
    setActiveSubjectId(subject.id);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('subjects.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" disabled={pending} onClick={() => void run(() => onCreate())}>
            {t('subjects.createSubmit')}
          </Button>
          {createApiError && <FormFeedback tone="error">{createApiError}</FormFeedback>}
        </div>
        {createdSubject && (
          <FormFeedback tone="success">
            {t('subjects.createdBanner', {
              provisionalIdentifier: createdSubject.provisionalIdentifier,
              id: createdSubject.id,
            })}
          </FormFeedback>
        )}

        {listState.status === 'loading' && (
          <p className="text-sm text-muted-foreground">{t('subjects.listLoading')}</p>
        )}
        {listState.status === 'error' && (
          <div className="space-y-2">
            <FormFeedback tone="error">{t('subjects.listError')}</FormFeedback>
            <Button type="button" variant="outline" size="sm" onClick={listState.reload}>
              {t('subjects.listRetry')}
            </Button>
          </div>
        )}
        {listState.status === 'ready' && listState.subjects.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('subjects.listEmpty')}</p>
        )}
        {listState.status === 'ready' && listState.subjects.length > 0 && (
          <>
            <ul aria-label={t('subjects.listLabel')} className="divide-border divide-y">
              {listState.subjects.map((subject) => (
                <li key={subject.id}>
                  <button
                    type="button"
                    onClick={() => onOpen(subject)}
                    aria-current={subject.id === activeSubjectId ? 'true' : undefined}
                    className="hover:bg-accent focus-visible:ring-ring flex w-full flex-wrap
                      items-center justify-between gap-3 rounded-md px-2 py-3 text-left
                      focus-visible:ring-2 focus-visible:outline-none"
                  >
                    <span className="flex flex-col">
                      <span className="text-sm font-medium">
                        {subject.definitiveIdentifier ?? subject.provisionalIdentifier}
                      </span>
                      {subject.definitiveIdentifier !== null && (
                        <span className="text-xs text-muted-foreground">
                          {subject.provisionalIdentifier}
                        </span>
                      )}
                    </span>
                    <span className="flex flex-wrap items-center gap-3">
                      {subject.actualTreatmentDate !== null && (
                        <span className="text-xs text-muted-foreground">
                          {formatDate(subject.actualTreatmentDate)}
                        </span>
                      )}
                      <span
                        className={`status-badge ${LIFECYCLE_BADGE_TONE[subject.lifecycleState]}`}
                      >
                        {t(LIFECYCLE_LABEL_KEY[subject.lifecycleState])}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">
              {t('subjects.listCount', { total: listState.subjects.length })}
            </p>
          </>
        )}

        {activeSubjectId !== null && (
          <div id="study-subject-workspace">
            <SubjectWorkspacePanel
              key={activeSubjectId}
              studyId={studyId}
              subjectId={activeSubjectId}
              initialSnapshot={createdSubject}
              reservedTreatmentDatePoolEntryId={
                (listState.status === 'ready'
                  ? listState.subjects.find((subject) => subject.id === activeSubjectId)
                      ?.reservedTreatmentDatePoolEntryId
                  : null) ?? null
              }
              onSubjectWritten={listState.reload}
              subjectList={listState}
              canReadSchedules={canReadSchedules}
              canReadPool={canReadPool}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
